import { FC, useCallback, useEffect, useState } from 'react'
import ReactDataSheet from 'react-datasheet'
import { produce } from 'immer'
import { useNavigate } from 'react-router-dom'

import hearingMessage from '../../../data/hearing-message.json'
import {
  ConfirmDialog,
  SupplierHearingHeader,
  BuildingInformation,
  ErrorMessages,
  ErrorMessage
} from '../../../components/parts'
import { QuotationRequestBuildingFooter } from './QuotationRequestBuildingFooter'
import { useAccessibleAuthority, useLeaveGuard, usePageTitle } from '../../../components/hooks'
import {
  useAppSelector, useAppDispatch, CommonActions
  , ConfirmReplyActions
} from '../../../stores'
import { GetSupplierHearingWorkQuantityListGateway } from '../../../gateway/supplier/GetSupplierHearingWorkQuantityListGateway'
import { PutSupplierHearingWorkQuantityListGateway } from '../../../gateway/supplier/PutSupplierHearingWorkQuantityListGateway'
import { Authority } from '../../../gateway/system/Authority'
import {
  GetBuildingItemReqParams,
  GetHearingQuantityListRes,
  WorkQuantity
} from '@gateway.Supplier'
import { QuotationRequestBuildingGrid } from './QuotationRequestBuildingGrid'

import './QuotationRequestBuilding.css'
import { WritableDraft } from 'immer/dist/internal'
import { MessageProvider } from '../../../services'

export type CellValue = number | null
export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}
export interface GridDataSate {
  grid: GridElement[][]
  workQuantityList: WorkQuantity[]
  workQuantityListLastMonth: WorkQuantity[]
  displayModes: boolean[]
}

const GRID_DATA_STATE: GridDataSate = {
  grid: [],
  workQuantityList: [],
  workQuantityListLastMonth: [],
  displayModes: []
}

const MONTHS = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3]
const YEARS = 6

const GET_BUILDING_ITEM_REQ_PARAMS: GetBuildingItemReqParams = {
  simulationYm: '',
  procurementNo: '',
  subNo: '',
  branchCd: '',
  expenseCd: '',
  expenseSeq: '',
  supplierCd: '',
  supplierBranchCd: ''
}

const HEARING_QUANTITY_LIST_RES: GetHearingQuantityListRes = {
  simulationYm: '',
  supplierCd: '',
  supplierBranchCd: '',
  factoryCd: '',
  expenseCd: '',
  targetHearingKbn: null,
  addStsCd: '',
  addStsCdBeforeChange: null,
  addStsCdAfterChange: null,
  addSts: '',
  workQuantityList: []
}

/**
 * Create GridData
 * @param workQuantityList
 * @param simulationYm
 * @param nonCtrlFlg
 * @param authority
 * @returns gridData
 */
const createData = (
  workQuantityList: WorkQuantity[],
  simulationYm: string,
  nonCtrlFlg: boolean,
  authority?: Authority
) => {
  const gridData: GridElement[][] = []

  // 労務数一覧が空の場合、合計行のみを作成する
  if (workQuantityList.length === 0) {
    gridData.push([])
    for (let i = 0; i < YEARS * MONTHS.length; i++) {
      gridData[0][i] = {
        value: 0,
        readOnly: true
      }
    }
    return gridData
  }

  for (let r = 0; r < workQuantityList.length; r++) {
    gridData[r] = []
    for (let fy = 0; fy < YEARS; fy++) {
      for (let m = 0; m < MONTHS.length; m++) {
        gridData[r][fy * 12 + m] = {
          value: null,
          readOnly: onDataModifiableFn((fy * 12 + m), simulationYm, authority) || nonCtrlFlg || workQuantityList[r].lostOrderFlg === 1
        }
      }
    }
  }

  workQuantityList.forEach((workQuantityObj, rowIndex) => {
    const workQuantityByMonthArr = workQuantityObj.workQuantityByMonth
    workQuantityByMonthArr.forEach((valByMonth, columnIndex) => {
      gridData[rowIndex][columnIndex].value = valByMonth
    })
  })

  // 合計のRowを追加
  gridData.push([])

  // 合計の計算
  for (let i = 0; i < gridData[0].length; i++) {
    let sum = 0
    for (let j = 0; j < gridData.length - 1; j++) {
      sum += Number(gridData[j][i].value) ?? 0
    }
    gridData[gridData.length - 1][i] = {
      value: sum,
      readOnly: true
    }
  }
  return gridData
}

/**
 * @description 合計の計算
 * @param draft WritableDraft<GridDataSate>
 * @param currentRowIndex number
 * @param lastRowIndex number
 */
const setTotalRowData = (
  draft: WritableDraft<GridDataSate>,
  currentRowIndex: number,
  lastRowIndex: number
) => {
  for (let i = 0; i < draft.grid[lastRowIndex].length; i++) {
    if (currentRowIndex === 0) {
      draft.grid[lastRowIndex][i].value = Number(
        draft.grid[currentRowIndex][i].value
      )
    } else {
      draft.grid[lastRowIndex][i].value =
        Number(draft.grid[lastRowIndex][i].value) +
        Number(draft.grid[currentRowIndex][i].value)
    }
  }
}

// カレンダの活性状態を設定
const onDataModifiableFn = (i: number, simulationYm: string, authority?: Authority) => {
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const year = new Date().getFullYear()
  const month = new Date().getMonth() + 1

  // シミュレーション年月度が過去の場合すべて非活性
  if (simulatedYear < year ||
      (simulatedYear === year && simulatedMonth < month)) {
    return true
  }

  // 過去月を非活性
  // 社内ユーザは過去月も入力可能とする
  if (authority === Authority.Builder || authority === Authority.Material || authority === Authority.Branch) {
    return false
  } else {
    if (month < 4) {
      return i < month + 8 + 12 ? i < 12 + 12 : false
    } else {
      return i < month - 4 + 12 ? i < 8 + 12 : false
    }
  }
}

/**
 * 前月度の取得
 * @param year
 * @param month
 * @returns 前月度
 */
const getPreMonth = (year: number, month: number) => {
  let year2 = year
  let month2 = month - 1
  if (month2 === 0) {
    year2 = year2 - 1
    month2 = 12
  }

  let month2Str = `${month2}`
  if (month2 < 10) {
    month2Str = `0${month2}`
  }
  return `${year2}${month2Str}`
}

export const QuotationRequestBuilding: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Material, Authority.Branch, Authority.Supplier)
  usePageTitle('見積依頼物件')

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  // 登録状況
  const regStatus = useAppSelector((state) => state.confirmreply.regStatus)

  const {
    replyYm,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    factoryNm,
    supplierBranchCd,
    supplierBranchNm
  } = useAppSelector((state) => state.confirmreply.confirmReplyInfo)

  const replyStsCd = useAppSelector((state) => state.confirmreply.replyStsCd)

  // ログイン情報の取得
  const userProp = useAppSelector((state) => state.common.userProp)
  const displayStartYear = useAppSelector((state) => state.common.displayStartYear)

  const [gridDataSate, setGridDataSate] = useState(GRID_DATA_STATE)
  const [getHearingQuantityListRes, setGetHearingQuantityListRes] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [getHearingQuantityListResLastMonth, setGetHearingQuantityListResLastMonth] =
    useState<GetHearingQuantityListRes>(HEARING_QUANTITY_LIST_RES)
  const [buildingInfoParams, setBuildingInfoParams] =
    useState<GetBuildingItemReqParams>(GET_BUILDING_ITEM_REQ_PARAMS)
  const [userAuthority, setUserAuthority] = useState<number>(Authority.None)

  const [displayMode, setDisplayMode] = useState<'0' | '1'>('1')
  const [confirmDialogSignInShow, setConfirmDialogSignInShow] = useState(false)
  const [confirmDialogTempSaveShow, setConfirmDialogTempSaveShow] = useState(false)
  const [buildingInformationWindowShow, setBuildingInformationWindowShow] =
    useState(false)
  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  // 変更検知
  const [isChanged, setIsChanged] = useState(false)

  // 過去月の判定フラグ
  const date = new Date()
  const currentYear = date.getFullYear()
  const currentMonth = date.getMonth() + 1
  const simulatedYear = Number(simulationYm.substring(0, 4))
  const simulatedMonth = Number(simulationYm.substring(4, simulationYm.length))
  const lastMonthFlg = simulatedYear < currentYear || (simulatedYear === currentYear && simulatedMonth < currentMonth)

  // 過去月または(業者ユーザかつ回答済)または確認済または(支店ユーザかつ自支店以外)の場合、入力項目を非活性にする
  const nonCtrlFlg =
    lastMonthFlg ||
    (userProp?.authority === Authority.Supplier && replyStsCd === '5') ||
    replyStsCd === '7' ||
    (userProp?.authority === Authority.Branch && userProp?.branchCode !== branchCd)

  const [, resetLeaveGuard] = useLeaveGuard([isChanged], '内容が編集中です。このページを離れますか？')

  // 表示非表示ボタン押下
  const handleOnHiddenBtnClick = () => {
    if (displayMode === '0') {
      setDisplayMode('1')
      setGridDataSate(
        produce((draft) => {
          gridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const isDisplay = obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
            const lastRowIndex = gridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex)

            draft.displayModes[rowIndex] = isDisplay
          })
        })
      )
    }
    if (displayMode === '1') {
      setDisplayMode('0')
      setGridDataSate(
        produce((draft) => {
          gridDataSate.workQuantityList.forEach((obj, rowIndex) => {
            const lastRowIndex = gridDataSate.grid.length - 1
            setTotalRowData(draft, rowIndex, lastRowIndex)

            draft.displayModes[rowIndex] = true
          })
        })
      )
    }
  }

  // 非表示チェックボックス押下
  const handleOnDisplayCheckboxChange = (
    selectedRowIndex: number,
    selected: boolean
  ) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[selectedRowIndex].hiddenFlg = Number(selected)
      })
    )
  }

  // 詳細ボタン押下
  const handleOnWorkQuantityBtnClick = (workQuantity: WorkQuantity) => {
    const { procurementNo, subNo, branchCd, expenseSeq } = workQuantity
    setBuildingInfoParams(
      produce((draft) => {
        draft.simulationYm = simulationYm
        draft.procurementNo = procurementNo
        draft.subNo = subNo
        draft.branchCd = branchCd
        draft.expenseCd = expenseCd
        draft.expenseSeq = expenseSeq
        draft.supplierCd = supplierCd
        draft.supplierBranchCd = supplierBranchCd
      })
    )

    setBuildingInformationWindowShow(!buildingInformationWindowShow)
  }

  // 備考の変更時
  const handleOnRemarkInputChange = (val: string, rowNo: number) => {
    setIsChanged(true)
    setGridDataSate(
      produce((draft) => {
        draft.workQuantityList[rowNo].remark = val
      })
    )
  }

  // カレンダ部の値変更時
  const handleCellsChanged = (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>
  ) => {
    setIsChanged(true)
    if (isNaN(Number(arrayOfChanges[0]?.value))) return

    setGridDataSate(
      produce((draft) => {
        arrayOfChanges.forEach(({ row, col, value }) => {
          const preCellVal = Number(draft.grid[row][col].value)
          const currentCellVal = Number(value)
          draft.grid[row][col].value = value

          draft.workQuantityList[row].workQuantityByMonth[col] = currentCellVal
          const preCellTotalVal = Number(
            draft.grid[gridDataSate.grid.length - 1][col].value
          )
          const currentCellTotalVal =
            preCellTotalVal - preCellVal + currentCellVal
          draft.grid[gridDataSate.grid.length - 1][col].value =
            currentCellTotalVal
        })
      })
    )
  }

  /** 登録ボタン押下 */
  const handleOnSaveBtnClick = () => {
    setConfirmDialogSignInShow(true)
  }

  // 一時保存ボタン押下
  const handleOnTempSaveBtnClick = () => {
    setConfirmDialogTempSaveShow(true)
  }

  // 登録確認ダイアログOKボタン押下
  const dialogConfirmSignInBtn = () => {
    setConfirmDialogSignInShow(false)

    const newQuantityList = produce(getHearingQuantityListRes, (draft) => {
      // ["3"(見積依頼物件)]
      draft.targetHearingKbn = ['3']
      draft.addStsCdBeforeChange = draft.addStsCd
      // "3"(登録済)
      draft.addStsCdAfterChange = '3'

      const workQuantityList: WorkQuantity[] = []
      gridDataSate.workQuantityList.forEach((obj) => {
        const workQuantity = Object.assign({}, obj)
        // 工事名手入力
        workQuantity.constructionNmManual = ''
        // 受注元手入力
        workQuantity.otherCompany = ''
        // 施工部位コード
        workQuantity.constructionPartCd = ''
        // 工程状況コード
        workQuantity.processStsCd = ''
        // 遅延程度コード
        workQuantity.delayLevelCd = ''
        // 決定区分
        workQuantity.decisionKbn = ''
        // 対象外フラグ
        workQuantity.notApplicableFlg = 0
        // 削除フラグ
        workQuantity.delFlg = 0

        workQuantityList.push(workQuantity)
      })

      draft.workQuantityList = workQuantityList
    })

    dispatch(CommonActions.setIsLoading(true))
    PutSupplierHearingWorkQuantityListGateway.putHearingQuantityList(
      { data: newQuantityList }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingQuantityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '登録')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  // 一時保存確認ダイアログOKボタン押下
  const dialogConfirmTempSaveBtn = () => {
    setConfirmDialogTempSaveShow(false)

    const newQuantityList = produce(getHearingQuantityListRes, (draft) => {
      // ["3"(見積依頼物件)]
      draft.targetHearingKbn = ['3']
      draft.addStsCdBeforeChange = draft.addStsCd
      // "2"(一時保存)
      draft.addStsCdAfterChange = '2'

      const workQuantityList: WorkQuantity[] = []
      gridDataSate.workQuantityList.forEach((obj) => {
        const workQuantity = Object.assign({}, obj)
        // 工事名手入力
        workQuantity.constructionNmManual = ''
        // 受注元手入力
        workQuantity.otherCompany = ''
        // 施工部位コード
        workQuantity.constructionPartCd = ''
        // 工程状況コード
        workQuantity.processStsCd = ''
        // 遅延程度コード
        workQuantity.delayLevelCd = ''
        // 決定区分
        workQuantity.decisionKbn = ''
        // 対象外フラグ
        workQuantity.notApplicableFlg = 0
        // 削除フラグ
        workQuantity.delFlg = 0

        workQuantityList.push(workQuantity)
      })

      draft.workQuantityList = workQuantityList
    })

    dispatch(CommonActions.setIsLoading(true))
    PutSupplierHearingWorkQuantityListGateway.putHearingQuantityList(
      { data: newQuantityList }
    )
      .then((res) => {
        if (!res.error) {
          fetchHearingQuantityList()
          if (isChanged) {
            resetLeaveGuard()
          }
          setNotificationMessages([
            {
              id: 'I004',
              content: MessageProvider.provide('I004', '一時保存')
            }
          ])
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }

  const handleOnCloseBuildingInfoWindow = () => {
    setBuildingInformationWindowShow(!buildingInformationWindowShow)
  }

  /** 戻るボタンをクリック */
  const handleOnGoBackBtnClick = () => {
    navigate(-1)
  }

  // 見積物件データを取得する
  const fetchHearingQuantityList = useCallback(() => {
    dispatch(CommonActions.setIsLoading(true))
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYm,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      '3', // 見積依頼物件
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setGetHearingQuantityListRes(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })

    // 先月の取得
    const simulationYmLastMonth = getPreMonth(simulatedYear, simulatedMonth)
    GetSupplierHearingWorkQuantityListGateway.getHearingQuantityList(
      simulationYmLastMonth,
      supplierCd,
      supplierBranchCd,
      factoryCd,
      expenseCd,
      '3', // 見積依頼物件
      displayStartYear
    )
      .then((res) => {
        if (!res.error) {
          setGetHearingQuantityListResLastMonth(res.data ?? HEARING_QUANTITY_LIST_RES)
        } else {
          const messages = res.messages.map((item) => ({
            id: item.id,
            content: item.message
          }))
          setErrorMessages(messages)
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
      })
  }, [expenseCd, factoryCd, simulationYm, supplierCd])

  // #region useEffect functions start
  useEffect(() => {
    // データストアチェック．業者ヒアリング遷移パラメータ．パラメータが設定されていない場合、エラーページへ遷移する
    const isPageError =
      regStatus === undefined ||
      regStatus === null ||
      simulationYm === '' ||
      supplierCd === '' ||
      factoryCd === '' ||
      expenseCd === '' ||
      replyYm === '' ||
      expense === '' ||
      supplierNm === '' ||
      branchCd === '' ||
      branchNm === '' ||
      supplierBranchCd === ''

    if (isPageError) {
      const pageParam = {
        regStatus,
        simulationYm,
        supplierCd,
        factoryCd,
        expenseCd,
        replyYm,
        expense,
        supplierNm,
        branchCd,
        branchNm,
        supplierBranchCd
      }
      throw new Error(`PageError Occurred. ${JSON.stringify(pageParam)}`)
    }
  }, [
    regStatus,
    simulationYm,
    supplierCd,
    factoryCd,
    expenseCd,
    replyYm,
    expense,
    supplierNm,
    branchCd,
    branchNm,
    supplierBranchCd,
    supplierBranchNm
  ])

  useEffect(() => {
    return () => { dispatch(ConfirmReplyActions.setSupplierEstimationBuildingLocation(0)) }
  }, [])

  useEffect(() => {
    fetchHearingQuantityList()
  }, [fetchHearingQuantityList])

  useEffect(() => {
    setGridDataSate(
      produce((draft) => {
        const workQuantityList = getHearingQuantityListRes.workQuantityList
        const workQuantityListLastMonth = getHearingQuantityListResLastMonth.workQuantityList

        const formatedWorkQuantityList = workQuantityList.map((item) => ({
          ...item,
          delFlg: 0
        }))

        draft.workQuantityList = formatedWorkQuantityList
        draft.workQuantityListLastMonth = workQuantityListLastMonth
        draft.grid = createData(
          formatedWorkQuantityList,
          simulationYm,
          nonCtrlFlg,
          userProp?.authority)

        const displayModes = workQuantityList.map((obj) => {
          return obj.hiddenFlg === 0 && obj.lostOrderFlg === 0
        })
        draft.displayModes = displayModes

        setUserAuthority(
          userProp === undefined ? Authority.None : userProp.authority
        )
      })
    )
  }, [getHearingQuantityListRes.workQuantityList, userProp])
  // #endregion useEffect functions end

  return (
    <>
      <SupplierHearingHeader
        regStatus={getHearingQuantityListRes.addStsCd}
        promptInfo={hearingMessage.quotationRequestBuilding.headerPromptInfo}
        supplierHeaderInfo={{
          replyYm,
          supplierNm,
          expense,
          branchNm,
          factoryNm,
          supplierBranchNm
        }}
      />
      <QuotationRequestBuildingGrid
        gridDataState={gridDataSate}
        years={YEARS}
        months={MONTHS}
        simulationYm={simulationYm}
        nonCtrlFlg={nonCtrlFlg}
        userAuthority={userAuthority}
        onDisplayCheckboxChange={handleOnDisplayCheckboxChange}
        onWorkQuantityBtnClick={handleOnWorkQuantityBtnClick}
        expenseCd={expenseCd}
        onCellsChanged={handleCellsChanged}
        onRemarkInputChange={handleOnRemarkInputChange}
        displayStartYear={displayStartYear}
      />
      <QuotationRequestBuildingFooter
        displayMode={displayMode}
        nonCtrlFlg={nonCtrlFlg}
        onHiddenBtnClick={handleOnHiddenBtnClick}
        onSaveBtnClick={handleOnSaveBtnClick}
        onGoBackBtnClick={handleOnGoBackBtnClick}
        onTempSaveBtnClick={handleOnTempSaveBtnClick}
      />
      {buildingInformationWindowShow && (
        <BuildingInformation
          onClose={handleOnCloseBuildingInfoWindow}
          buildingInfoParams={buildingInfoParams}
        />
      )}

      {confirmDialogSignInShow && (
        <ConfirmDialog
          title="登録"
          open={confirmDialogSignInShow}
          renderCloseButton
          id={'I007'}
          args={['登録']}
          onClickOk={dialogConfirmSignInBtn}
          onClickCancel={() => setConfirmDialogSignInShow(false)}
        />
      )}
      {confirmDialogTempSaveShow && (
        <ConfirmDialog
          title="一時保存"
          open={confirmDialogTempSaveShow}
          renderCloseButton
          id={'I007'}
          args={['一時保存']}
          onClickOk={dialogConfirmTempSaveBtn}
          onClickCancel={() => setConfirmDialogTempSaveShow(false)}
        />
      )}
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </>
  )
}
